import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, collection, onSnapshot, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { IconArrowLeft } from '@tabler/icons-react';
import { call, PACK_STATUS, PARTY_FIELDS, KIND_LABEL, Chip, fmt, money, openStoragePdf, banner } from './packsApi';

export default function PackDetail() {
  const { packId } = useParams();
  const navigate = useNavigate();
  const [pack, setPack] = useState(undefined);
  const [docs, setDocs] = useState([]);
  const [sigs, setSigs] = useState([]);
  const [instalments, setInstalments] = useState([]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    const u = [
      onSnapshot(doc(db, 'signingPacks', packId), s => setPack(s.exists() ? { id: s.id, ...s.data() } : null), e => setMsg({ tone: 'danger', text: e.message })),
      onSnapshot(query(collection(db, 'signingPacks', packId, 'documents'), orderBy('order')), s => setDocs(s.docs.map(d => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, 'signingPacks', packId, 'signatures'), s => setSigs(s.docs.map(d => ({ id: d.id, ...d.data() })))),
      onSnapshot(query(collection(db, 'instalments'), where('packId', '==', packId)), s => setInstalments(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => a.number - b.number))),
    ];
    return () => u.forEach(f => f());
  }, [packId]);

  const back = <Button variant="ghost" icon={<IconArrowLeft size={14} stroke={1.5} />} onClick={() => navigate('/packs')}>Packs</Button>;
  if (pack === undefined) return <Layout><PageHeader title="Pack" subtitle="Loading..." actions={back} /></Layout>;
  if (pack === null) return <Layout><PageHeader title="Pack not found" actions={back} /></Layout>;

  const run = async (key, fn, ok) => {
    setBusy(key); setMsg(null);
    try { await fn(); if (ok) setMsg({ tone: 'success', text: ok }); } catch (e) { setMsg({ tone: 'danger', text: e.message }); }
    setBusy(null);
  };
  const open = (path) => run(path, () => openStoragePdf(path));
  const [label, tone] = PACK_STATUS[pack.status] || [pack.status, 'none'];
  const canVoid = !['void', 'paid'].includes(pack.status);

  return (
    <Layout>
      <PageHeader title={pack.title} subtitle={`${pack.clientName} · ${pack.projectName}`} actions={<>
        {back}
        {pack.status === 'draft' && <Button loading={busy === 'send'} onClick={() => window.confirm(`Send to ${pack.signer.email}?`) && run('send', () => call('sendPack', { packId }), `Sent to ${pack.signer.email}.`)}>Send</Button>}
        {canVoid && <Button variant="danger" loading={busy === 'void'} onClick={() => window.confirm('Void this pack? The link stops working straight away.') && run('void', () => call('voidPack', { packId }), 'Pack voided.')}>Void</Button>}
      </>} />
      <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 1100 }}>
        {msg && <div style={banner(msg.tone)}>{msg.text}</div>}
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, fontSize: 12 }}>
            <Info label="Status"><Chip label={label} tone={tone} /></Info>
            <Info label="Signer">{pack.signer.name}<div style={{ color: 'var(--color-text-tertiary)' }}>{pack.signer.email}</div></Info>
            <Info label="Sent">{fmt(pack.sentAt)}{pack.expiresAt && <div style={{ color: 'var(--color-text-tertiary)' }}>expires {fmt(pack.expiresAt)}</div>}</Info>
            <Info label="Signed">{fmt(pack.completedAt)}</Info>
            {Object.keys(pack.custom || {}).length > 0 && <Info label="Pack values" span={4}>{Object.entries(pack.custom).map(([k, v]) => `${k}: ${v}`).join(' · ')}</Info>}
          </div>
        </Card>

        <Card>
          <div style={{ fontSize: 14, marginBottom: 8 }}>Client details {pack.partyConfirmedAt ? `(confirmed ${fmt(pack.partyConfirmedAt)})` : '(not yet entered)'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px 14px', fontSize: 12 }}>
            {PARTY_FIELDS.map(([k, l]) => (
              <div key={k}><span style={{ color: 'var(--color-text-tertiary)' }}>{l}: </span>
                <span style={{ whiteSpace: 'pre-wrap' }}>{pack.partyDetails?.[k] || (pack.partyPrefill?.[k] ? `${pack.partyPrefill[k]} (prefilled)` : '—')}</span></div>
            ))}
          </div>
        </Card>

        <Card padding={0}>
          <div style={{ padding: '12px 16px 4px', fontSize: 14 }}>Documents and signature evidence</div>
          {docs.map(d => {
            const s = sigs.find(x => x.docId === d.id);
            return (
              <div key={d.id} style={{ display: 'grid', gridTemplateColumns: '1.3fr 2fr 200px', gap: 12, padding: '12px 16px', borderTop: '0.5px solid var(--color-border-light)', fontSize: 12, alignItems: 'start' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{d.order}. {d.title}</div>
                  <div style={{ color: 'var(--color-text-tertiary)', fontSize: 11 }}>{KIND_LABEL[d.kind]}{d.templateVersion ? ` · template v${d.templateVersion}` : ''}</div>
                  <div style={{ marginTop: 4 }}><Chip label={d.status === 'signed' ? 'Signed' : d.pdfPath ? 'Ready to sign' : 'Not rendered yet'} tone={d.status === 'signed' ? 'good' : 'none'} /></div>
                </div>
                <div style={{ fontSize: 11, lineHeight: 1.6 }}>
                  {s ? <>
                    <div>{s.signerName}, {s.signerRole} · {s.signerEmail}</div>
                    <div>{fmt(s.signedAt)} · IP {s.ip || '—'}</div>
                    <div style={{ color: s.docSha256 === d.sha256 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                      Hash {s.docSha256 === d.sha256 ? 'matches' : 'DOES NOT MATCH'} the rendered document</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--color-text-tertiary)', wordBreak: 'break-all' }}>{s.docSha256}</div>
                  </> : <span style={{ color: 'var(--color-text-tertiary)' }}>Not signed</span>}
                </div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {d.pdfPath && <Button size="sm" variant="secondary" loading={busy === d.pdfPath} onClick={() => open(d.pdfPath)}>Rendered</Button>}
                  {d.signedPdfPath && <Button size="sm" loading={busy === d.signedPdfPath} onClick={() => open(d.signedPdfPath)}>Signed PDF</Button>}
                </div>
              </div>
            );
          })}
        </Card>

        {pack.payment && (
          <Card>
            <div style={{ fontSize: 14, marginBottom: 8 }}>Payment</div>
            {pack.payment.instalments.map((i, n) => {
              const live = instalments.find(x => x.number === n + 1);
              const st = { due_now: ['Due at signing', 'warn'], scheduled: ['Scheduled', 'none'], invoicing: ['Invoicing', 'info'], invoiced: ['Invoiced', 'info'], paid: ['Paid', 'good'], failed: ['Failed', 'bad'] }[live?.status] || ['Not started', 'none'];
              return (
                <div key={n} style={{ display: 'grid', gridTemplateColumns: '110px 110px 1fr 120px', gap: 10, fontSize: 12, padding: '6px 0', borderTop: '0.5px solid var(--color-border-light)', alignItems: 'center' }}>
                  <div>Instalment {n + 1}</div>
                  <div>{money(i.amount, pack.payment.currency)}</div>
                  <div style={{ color: 'var(--color-text-tertiary)' }}>
                    {live?.dueDate ? `Due ${fmt(live.dueDate).split(',')[0]}` : i.dueOffsetDays === 0 ? 'At signing' : `${i.dueOffsetDays} days after signing`}
                    {live?.stripeInvoiceId && <> · <a href={`https://dashboard.stripe.com/invoices/${live.stripeInvoiceId}`} target="_blank" rel="noreferrer" style={{ color: 'var(--color-brand)' }}>Stripe invoice</a></>}
                    {live?.paidAt && ` · paid ${fmt(live.paidAt)}`}
                  </div>
                  <div><Chip label={st[0]} tone={st[1]} /></div>
                </div>
              );
            })}
            {pack.payment.stripeCustomerId && <div style={{ fontSize: 11, marginTop: 6 }}><a href={`https://dashboard.stripe.com/customers/${pack.payment.stripeCustomerId}`} target="_blank" rel="noreferrer" style={{ color: 'var(--color-brand)' }}>Stripe customer</a></div>}
          </Card>
        )}
      </div>
    </Layout>
  );
}

function Info({ label, children, span = 1 }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}
